import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, OnDocumentLoadSuccess } from 'react-pdf';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import '../../utils/pdfjs-worker';
import { BRAND } from '../../theme';

type FitMode = 'width' | 'page' | 'actual';

const FIT_OPTIONS: Array<{ value: FitMode; label: string }> = [
  { value: 'width', label: 'Fit Width' },
  { value: 'page', label: 'Fit Page' },
  { value: 'actual', label: 'Actual Size' }
];

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
// react-pdf renders at 72dpi CSS pixels per point; a letter page is 612pt wide.
const BASE_PAGE_WIDTH = 612;
const BASE_PAGE_HEIGHT = 792;

interface PdfReviewPaneProps {
  /** Reports the loaded document's paging so the workspace header can show it */
  onDocumentStateChange?: (state: { page: number; pageCount: number | null }) => void;
}

export interface PdfReviewPaneRef {
  nextPage: () => void;
  previousPage: () => void;
}

const PdfReviewPane = React.forwardRef<PdfReviewPaneRef, PdfReviewPaneProps>(
  ({ onDocumentStateChange }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // Horizontal padding around the page inside the viewport (both sides).
    const viewportGutter = isMobile ? 16 : 32;
    const [file, setFile] = useState<File | null>(null);
    const [pageCount, setPageCount] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [scale, setScale] = useState(1);
    const [fitMode, setFitMode] = useState<FitMode>('width');
    const [rotation, setRotation] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [viewportWidth, setViewportWidth] = useState(0);
    const viewportRef = useRef<HTMLDivElement | null>(null);

    // Track the available width so "Fit Width"/"Fit Page" stay correct as the
    // splitter moves or the window resizes.
    useEffect(() => {
      const element = viewportRef.current;
      if (!element || typeof ResizeObserver === 'undefined') return undefined;

      const observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) setViewportWidth(entry.contentRect.width);
      });
      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    useEffect(() => {
      onDocumentStateChange?.({ page, pageCount });
    }, [page, pageCount, onDocumentStateChange]);

    // A rotated page is measured across its short edge.
    const quarterTurned = rotation % 180 !== 0;
    const naturalWidth = quarterTurned ? BASE_PAGE_HEIGHT : BASE_PAGE_WIDTH;
    const naturalHeight = quarterTurned ? BASE_PAGE_WIDTH : BASE_PAGE_HEIGHT;

    const effectiveScale = (() => {
      // Nothing to fit to until a document is loaded.
      if (!file) return 1;
      if (fitMode === 'actual' || viewportWidth === 0) return scale;
      const available = Math.max(viewportWidth - viewportGutter, 200);
      if (fitMode === 'width') return available / naturalWidth;
      const viewportHeight = viewportRef.current?.clientHeight || 0;
      if (viewportHeight === 0) return available / naturalWidth;
      return Math.min(
        available / naturalWidth,
        (viewportHeight - viewportGutter) / naturalHeight
      );
    })();

    const changeScale = (delta: number) => {
      // Zooming is an explicit choice, so it takes over from the fit modes.
      setScale(current => {
        const from = fitMode === 'actual' ? current : effectiveScale;
        return Math.min(MAX_SCALE, Math.max(MIN_SCALE, parseFloat((from + delta).toFixed(2))));
      });
      setFitMode('actual');
    };

    const goToPage = useCallback(
      (next: number) => {
        setPage(current => {
          const target = Math.min(Math.max(next, 1), pageCount || 1);
          return target === current ? current : target;
        });
      },
      [pageCount]
    );

    React.useImperativeHandle(ref, () => ({
      nextPage: () => goToPage(page + 1),
      previousPage: () => goToPage(page - 1)
    }));

    const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.[0];
      if (!selected) return;
      if (!selected.name.toLowerCase().endsWith('.pdf')) {
        setError('Please select a valid PDF file');
        return;
      }
      setFile(selected);
      setError(null);
      setLoading(true);
      setPage(1);
      setRotation(0);
      setFitMode('width');
      // Allow re-picking the same file after a reset.
      event.target.value = '';
    };

    const onDocumentLoadSuccess = ({ numPages }: OnDocumentLoadSuccess) => {
      setPageCount(numPages);
      setPage(1);
      setLoading(false);
      setError(null);
    };

    const onDocumentLoadError = (loadError: Error) => {
      console.error('Error loading PDF:', loadError);
      setError('Failed to load PDF. Please try again with a different file.');
      setLoading(false);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
        {/* Toolbar — phones: file + paging on top, view controls underneath */}
        {isMobile ? (
          <Box sx={{ borderBottom: `1px solid ${BRAND.border}`, px: 1.5, py: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{ flex: 1, height: 44 }}
              >
                {file ? 'Change PDF' : 'Upload PDF'}
                <input type="file" hidden accept=".pdf,application/pdf" onChange={onFileChange} />
              </Button>
              <IconButton
                aria-label="Previous page"
                disabled={!file || page <= 1}
                onClick={() => goToPage(page - 1)}
                sx={{ width: 44, height: 44, border: `1px solid ${BRAND.border}`, borderRadius: '10px' }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 44, textAlign: 'center' }}
              >
                {pageCount ? `${page} / ${pageCount}` : '– / –'}
              </Typography>
              <IconButton
                aria-label="Next page"
                disabled={!file || page >= (pageCount || 1)}
                onClick={() => goToPage(page + 1)}
                sx={{ width: 44, height: 44, border: `1px solid ${BRAND.border}`, borderRadius: '10px' }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
            {file && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <IconButton
                  aria-label="Zoom out"
                  onClick={() => changeScale(-0.1)}
                  disabled={effectiveScale <= MIN_SCALE}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 44, textAlign: 'center', fontWeight: 600 }}>
                  {Math.round(effectiveScale * 100)}%
                </Typography>
                <IconButton
                  aria-label="Zoom in"
                  onClick={() => changeScale(0.1)}
                  disabled={effectiveScale >= MAX_SCALE}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Rotate 90°"
                  onClick={() => setRotation(current => (current + 90) % 360)}
                >
                  <Rotate90DegreesCwIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1 }} />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select value={fitMode} onChange={(e) => setFitMode(e.target.value as FitMode)}>
                    {FIT_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        ) : (
          <>
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${BRAND.border}`
          }}
        >
          <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
            Upload PDF
            <input type="file" hidden accept=".pdf,application/pdf" onChange={onFileChange} />
          </Button>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={() => changeScale(-0.1)}
                disabled={!file || effectiveScale <= MIN_SCALE}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center', fontWeight: 600 }}>
            {Math.round(effectiveScale * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={() => changeScale(0.1)}
                disabled={!file || effectiveScale >= MAX_SCALE}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Rotate 90°">
            <span>
              <IconButton
                size="small"
                onClick={() => setRotation(current => (current + 90) % 360)}
                disabled={!file}
              >
                <Rotate90DegreesCwIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <FormControl size="small" sx={{ minWidth: 132, ml: 0.5 }}>
            <Select
              value={fitMode}
              onChange={(e) => setFitMode(e.target.value as FitMode)}
              disabled={!file}
            >
              {FIT_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            startIcon={<ChevronLeftIcon />}
            disabled={!file || page <= 1}
            onClick={() => goToPage(page - 1)}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {pageCount ? `Page ${page} of ${pageCount}` : 'No document'}
          </Typography>
          <Button
            size="small"
            endIcon={<ChevronRightIcon />}
            disabled={!file || page >= (pageCount || 1)}
            onClick={() => goToPage(page + 1)}
          >
            Next
          </Button>
        </Box>
          </>
        )}

        {/* Document */}
        <Box
          ref={viewportRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#F1F5F9',
            display: 'flex',
            // Children centre with auto margins so a zoomed-in page can still be
            // scrolled to its left edge (justify-content: center would clip it).
            justifyContent: 'flex-start',
            '& > *': { mx: 'auto' },
            alignItems: 'flex-start',
            p: { xs: 1, md: 2 },
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {error ? (
            <Typography color="error" sx={{ mt: 4 }}>
              {error}
            </Typography>
          ) : !file ? (
            <Box sx={{ textAlign: 'center', mt: 8, px: 3 }}>
              <UploadFileIcon sx={{ fontSize: 46, color: BRAND.textMuted, mb: 1 }} />
              <Typography color="text.secondary">
                {isMobile
                  ? 'Upload a PDF, then flip between it and the form.'
                  : 'Upload a PDF to review it next to the form.'}
              </Typography>
            </Box>
          ) : (
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 6 }}>
                  <CircularProgress />
                  <Typography>Loading PDF...</Typography>
                </Box>
              }
            >
              {pageCount ? (
                <Box sx={{ boxShadow: 3, backgroundColor: '#fff' }}>
                  <Page
                    pageNumber={page}
                    scale={effectiveScale}
                    rotate={rotation}
                    loading={
                      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
                        <CircularProgress size={24} />
                      </Box>
                    }
                    error={<Typography color="error">Error loading page.</Typography>}
                  />
                </Box>
              ) : null}
            </Document>
          )}
          {loading && !pageCount && !error && <Box sx={{ display: 'none' }} />}
        </Box>
      </Box>
    );
  }
);

PdfReviewPane.displayName = 'PdfReviewPane';

export default PdfReviewPane;
